import { Link } from 'react-router-dom';

/**
 * Aviso de Privacidad genérico de la plataforma (LFPDPPP MX).
 *
 * Cubre los requisitos mínimos de la LFPDPPP (México) Art. 15-18 + RLFPDPPP
 * Art. 14-29 + Lineamientos del Aviso de Privacidad (DOF 17/01/2013).
 *
 * Los Términos de Uso, SLA y Limitación de Responsabilidad viven en una
 * página separada (/sla) — este documento es ÚNICAMENTE el Aviso de
 * Privacidad LFPDPPP. No se mezclan los dos por separación legal limpia.
 *
 * ESTADO: BORRADOR vigente. Requiere validación de abogado con cédula
 * profesional en protección de datos personales antes de uso en producción
 * de pago. Mientras tanto se publica con disclaimer arriba para que los
 * Titulares puedan ejercer derechos ARCO; no constituye base jurídicamente
 * blindada en su forma actual.
 */
export function AvisoPrivacidad() {
  return (
    <main style={{ maxWidth: 820, margin: '0 auto', padding: '3rem 1.5rem 5rem', fontSize: 14, lineHeight: 1.65, color: 'var(--text-mid)' }}>
      <Link to="/" style={{ fontSize: 13, color: 'var(--text-dim)' }}>
        ← Volver
      </Link>

      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 30, marginTop: 16, color: 'var(--text)' }}>
        Aviso de Privacidad
      </h1>
      <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 4 }}>
        Última actualización: 2026-05-11 · Vigente desde: 2026-05-11
      </p>

      {/* ============================================================== */}
      {/* DISCLAIMER OBLIGATORIO arriba del documento                      */}
      {/* ============================================================== */}
      <div style={{
        marginTop: 24,
        padding: '14px 18px',
        background: 'rgba(255, 200, 100, 0.08)',
        border: '1px solid rgba(255, 200, 100, 0.3)',
        borderRadius: 8,
        fontSize: 12.5,
      }}>
        <strong style={{ color: 'var(--text)' }}>⚠ Documento en revisión legal</strong>
        <br />
        Este Aviso es un borrador vigente. Está pendiente de validación por
        un abogado con cédula profesional en protección de datos personales.
        Cualquier discrepancia con la versión final aprobada se notificará
        con 15 días naturales de anticipación a los Titulares y al
        Responsable, conforme al Art. 18 LFPDPPP.
      </div>

      {/* ============================================================== */}
      {/* SECCIÓN 1 — Identidad del Responsable y Encargado                */}
      {/* ============================================================== */}
      <H2>1. Identidad de las partes</H2>
      <p>
        <strong>Responsable del tratamiento</strong> (Art. 3 fracc. XIV
        LFPDPPP): la persona moral o física que contrata la plataforma y
        que determina las finalidades y medios del tratamiento de los datos
        personales de sus Titulares (en adelante, "el Cliente" o "el
        Responsable"). El Cliente publica sus propios datos de contacto y
        canal ARCO en su sitio institucional y/o en el documento contractual
        que mantiene con cada Titular.
      </p>
      <p>
        <strong>Encargado del tratamiento</strong> (Art. 3 fracc. IX
        LFPDPPP): <strong>aosenuma</strong>, opera la plataforma técnica que
        aloja, cifra y procesa los datos por cuenta del Responsable, bajo
        contrato de encargo (Art. 50-55 RLFPDPPP). Aosenuma NO determina las
        finalidades del tratamiento; únicamente ejecuta instrucciones
        documentadas del Responsable. Sitio oficial:{' '}
        <a href="https://aosenuma.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--btn-primary-bg, #b4c96f)', textDecoration: 'underline' }}>aosenuma.com</a>.
      </p>

      {/* ============================================================== */}
      {/* SECCIÓN 2 — Datos personales que serán tratados                  */}
      {/* ============================================================== */}
      <H2>2. Datos personales que serán tratados</H2>
      <p>
        Para las finalidades descritas en la sección 3, el Responsable
        tratará los siguientes datos del Titular (dueño, posesionario,
        ejidatario, representante legal u otra figura aplicable de tierras
        u otros bienes involucrados en el proyecto del Cliente):
      </p>
      <ul style={listStyle}>
        <li><strong>De identificación:</strong> nombre completo, CURP, RFC, clave de elector (INE), nacionalidad, firma autógrafa.</li>
        <li><strong>De contacto:</strong> domicilio, teléfono, correo electrónico.</li>
        <li><strong>Patrimoniales / financieros:</strong> CLABE bancaria (para pagos), folios RAN (derechos / tierras), clave catastral, coordenadas geoespaciales del predio, áreas (ha), información de tenencia (ejidatario / comunero / propietario privado / etc.).</li>
        <li><strong>Documentales:</strong> copias de INE, comprobante de domicilio, actas (nacimiento, matrimonio), constancias agrarias, contratos firmados.</li>
        <li><strong>De auditoría operacional:</strong> registro de eventos del sistema, IP, navegador y rol del usuario que accede al registro del titular.</li>
      </ul>
      <p>
        <strong>Datos sensibles:</strong> el sistema NO trata datos sensibles
        en el sentido del Art. 3 fracc. VI LFPDPPP (origen racial/étnico,
        estado de salud, información genética, creencias religiosas, opiniones
        políticas, preferencias sexuales). Si en el futuro alguna finalidad
        los requiriera, se obtendrá consentimiento expreso y por escrito
        adicional, en cumplimiento del Art. 9.
      </p>

      {/* ============================================================== */}
      {/* SECCIÓN 3 — Finalidades                                          */}
      {/* ============================================================== */}
      <H2>3. Finalidades del tratamiento</H2>
      <p><strong>Finalidades primarias</strong> (necesarias para la relación jurídica; sin ellas no es viable la prestación del servicio al titular):</p>
      <ul style={listStyle}>
        <li>Identificación del Titular y verificación de personalidad jurídica.</li>
        <li>Negociación, formalización y administración de contratos de arrendamiento, servidumbre voluntaria, compraventa u otras figuras aplicables sobre las tierras u otros bienes del Titular vinculados al proyecto del Cliente.</li>
        <li>Procesamiento de pagos al Titular conforme al contrato firmado con el Cliente.</li>
        <li>Gestión catastral, jurídica y de regularización ante el RAN, Registro Público de la Propiedad o autoridades equivalentes.</li>
        <li>Cumplimiento de obligaciones fiscales, contables y regulatorias aplicables al Cliente.</li>
        <li>Atención a solicitudes ARCO, quejas, denuncias e incidentes de seguridad.</li>
        <li>Auditoría interna y externa del cumplimiento legal del tratamiento.</li>
      </ul>
      <p><strong>Finalidades secundarias</strong> (NO requieren conformidad para el servicio principal — el Titular puede oponerse sin afectar la relación):</p>
      <ul style={listStyle}>
        <li>Comunicaciones informativas sobre el avance del proyecto del Cliente en la localidad del Titular.</li>
        <li>Invitación a eventos comunitarios o de consulta vinculados al proyecto.</li>
        <li>Análisis estadístico agregado y anonimizado para mejora del sistema (sin identificar al Titular individual).</li>
      </ul>
      <p style={{ background: 'rgba(255,255,255,0.04)', padding: 12, borderRadius: 6, fontSize: 13 }}>
        Para oponerse a las finalidades secundarias, el Titular puede enviar
        su solicitud al canal ARCO publicado por el Responsable (Cliente),
        indicando "OPOSICIÓN A FINALIDADES SECUNDARIAS" en el asunto, en
        cualquier momento y sin consecuencia para las finalidades primarias.
      </p>

      {/* ============================================================== */}
      {/* SECCIÓN 4 — Transferencias y remisiones                          */}
      {/* ============================================================== */}
      <H2>4. Transferencias y remisiones de datos</H2>
      <p>
        Los datos pueden ser <strong>remitidos</strong> (no requiere
        consentimiento, Art. 36 LFPDPPP) a Aosenuma como Encargado para la
        operación técnica de la plataforma, bajo contrato de encargo que
        prohíbe usos distintos a las finalidades del Responsable.
      </p>
      <p>Pueden ser <strong>transferidos</strong> a los siguientes terceros, con su consentimiento implícito al aceptar este Aviso:</p>
      <ul style={listStyle}>
        <li><strong>Proveedores de infraestructura en la nube:</strong> Supabase (Postgres + Auth, alojado en Amazon Web Services), Cloudflare (CDN, Workers y storage R2, jurisdicción global) y demás proveedores listados en el Anexo de Sub-encargados que mantiene Aosenuma. Esta transferencia es internacional y necesaria para la operación.</li>
        <li><strong>Autoridades competentes</strong> en los términos de cualquier requerimiento legal vigente (SAT, INAI, juzgados, fiscalías, RAN, Registro Público de la Propiedad).</li>
        <li><strong>Notarios, abogados externos y auditores</strong> contratados por el Responsable para la formalización de contratos y cumplimiento legal.</li>
      </ul>
      <p>El titular puede oponerse a las transferencias no necesarias enviando solicitud al canal ARCO indicado en la sección 5.</p>

      {/* ============================================================== */}
      {/* SECCIÓN 5 — Derechos ARCO + autoridad                            */}
      {/* ============================================================== */}
      <H2>5. Derechos ARCO y autoridad supervisora</H2>
      <p>
        En términos de los Arts. 22-31 LFPDPPP, el titular tiene derecho de:
      </p>
      <ul style={listStyle}>
        <li><strong>Acceso:</strong> conocer qué datos suyos tratamos y para qué fin.</li>
        <li><strong>Rectificación:</strong> corregir datos inexactos o incompletos.</li>
        <li><strong>Cancelación:</strong> solicitar la eliminación cuando no sean necesarios para las finalidades.</li>
        <li><strong>Oposición:</strong> oponerse al tratamiento para finalidades específicas (especialmente las secundarias).</li>
        <li><strong>Revocación del consentimiento:</strong> retirar la conformidad otorgada (cuando aplique).</li>
        <li><strong>Limitación del uso o divulgación:</strong> solicitar restricción temporal en casos específicos.</li>
        <li><strong>Portabilidad:</strong> recibir copia estructurada de sus datos en formato máquina-legible.</li>
      </ul>
      <p>
        <strong>Procedimiento:</strong> el Titular envía solicitud por
        escrito al canal ARCO publicado por el Responsable (Cliente),
        incluyendo: (i) nombre completo del Titular; (ii) copia de
        identificación oficial (INE o pasaporte) para acreditar identidad;
        (iii) descripción clara y precisa del derecho que se ejerce;
        (iv) datos personales sobre los que recae; (v) cualquier otro
        documento que facilite la atención. El Responsable tiene 20 días
        hábiles para responder (Art. 32 LFPDPPP) y 15 días adicionales para
        ejecutar la solicitud si procede. Aosenuma, como Encargado, asistirá
        al Responsable con la información técnica necesaria para atender
        estas solicitudes en plazos razonables.
      </p>
      <p>
        <strong>Autoridad supervisora:</strong> Instituto Nacional de Transparencia,
        Acceso a la Información y Protección de Datos Personales (INAI). En
        caso de respuesta insatisfactoria por parte del Responsable, el
        titular puede iniciar Procedimiento de Protección de Derechos ante
        INAI dentro de los 15 días hábiles siguientes (Art. 45 LFPDPPP). Más
        información: <a href="https://www.inai.org.mx/" target="_blank" rel="noopener noreferrer">www.inai.org.mx</a>.
      </p>

      {/* ============================================================== */}
      {/* SECCIÓN 6 — Mecanismos para limitar uso o divulgación            */}
      {/* ============================================================== */}
      <H2>6. Mecanismos para limitar el uso o divulgación</H2>
      <p>
        Los datos del titular están protegidos mediante:
      </p>
      <ul style={listStyle}>
        <li>Cifrado AES-256-GCM a nivel de campo para datos sensibles (nombre completo, teléfono, INE, CURP, RFC, dirección, CLABE) con llaves rotables alojadas en Cloudflare Secrets.</li>
        <li>TLS 1.3 obligatorio en todas las conexiones cliente ↔ servidor y servidor ↔ proveedores.</li>
        <li>Autenticación multifactor (MFA TOTP) obligatoria para todos los roles que acceden a paneles autenticados, con TTL de 8 horas para revelado de datos completos.</li>
        <li>Row-Level Security a nivel base de datos (Postgres RLS) que aísla cada cliente: ningún cliente puede ver datos de otro tenant.</li>
        <li>Bitácora de auditoría completa (eventos_negociacion): toda revelación de PII queda registrada con actor, IP, navegador, timestamp y motivo.</li>
        <li>Política de retención: los datos se conservan durante la vigencia de la relación contractual + 10 años para cumplimiento fiscal (Art. 30 Código Fiscal), tras lo cual se anonimizan o eliminan.</li>
      </ul>

      {/* Los Términos de Uso, SLA y Limitación de Responsabilidad NO viven
          aquí: este documento es ÚNICAMENTE el Aviso de Privacidad LFPDPPP.
          Para esas cláusulas (uptime, SLA, limitación de responsabilidad,
          fuerza mayor, jurisdicción y resolución de controversias), ver el
          documento separado disponible en el footer de la plataforma. */}

      {/* ============================================================== */}
      {/* SECCIÓN 7 — Cambios al Aviso                                     */}
      {/* ============================================================== */}
      <H2>7. Cambios al Aviso de Privacidad</H2>
      <p>
        Los cambios materiales a este Aviso (finalidades, terceros,
        derechos) se notificarán a los Titulares con un mínimo de{' '}
        <strong>15 días naturales</strong> de anticipación a su entrada en
        vigor, mediante publicación en esta misma URL y notificación por
        los canales que el Responsable mantenga con los Titulares
        registrados, conforme al Art. 18 LFPDPPP. Cambios menores
        (correcciones de redacción, actualización de canales de contacto)
        podrán publicarse sin notificación previa.
      </p>

      {/* ============================================================== */}
      {/* SECCIÓN 8 — Consentimiento                                       */}
      {/* ============================================================== */}
      <H2>8. Manifestación del consentimiento</H2>
      <p>
        El uso continuado de la plataforma posterior a la lectura de este
        Aviso constituye <strong>consentimiento tácito</strong> (Art. 8
        LFPDPPP) sobre el tratamiento descrito para finalidades primarias.
        Para datos patrimoniales y financieros que requieren consentimiento
        expreso, el Titular firma documento separado al iniciar la relación
        contractual con el Responsable. La autorización del Titular al
        ingresar el código TOTP de autenticación multifactor constituye
        además acto positivo de consentimiento informado para la revelación
        intra-organizacional de su PII durante la sesión activa (8 horas),
        registrado en bitácora.
      </p>

      {/* ============================================================== */}
      {/* DISCLAIMER FINAL DE BORRADOR                                     */}
      {/* ============================================================== */}
      <div style={{
        marginTop: 32,
        padding: '14px 18px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 8,
        fontSize: 12,
        color: 'var(--text-dim)',
      }}>
        <strong>Nota técnica:</strong> este documento es un borrador
        publicado para cumplimiento operativo inmediato. Está pendiente de
        revisión por un abogado con cédula profesional en protección de
        datos personales (LFPDPPP) y derecho mercantil mexicano. Cualquier
        discrepancia con la versión final aprobada se notificará conforme
        a la sección 7. Las cláusulas técnicas de protección reflejan
        práctica estándar de la industria y no constituyen asesoría legal.
      </div>

      <p style={{ marginTop: 24, fontSize: 12, color: 'var(--text-dim)' }}>
        El código fuente, diseños, marcas y modelos de datos de esta
        plataforma son <strong>propiedad intelectual exclusiva de
        Aosenuma</strong>. No constituyen software de código abierto.
      </p>
    </main>
  );
}

const listStyle: React.CSSProperties = {
  paddingLeft: 22,
  marginTop: 8,
  marginBottom: 12,
};

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{
      fontFamily: 'var(--font-display)',
      fontSize: 19,
      marginTop: 36,
      marginBottom: 8,
      color: 'var(--text)',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      paddingBottom: 6,
    }}>
      {children}
    </h2>
  );
}

